/**
 * AdminContext
 *
 * Global authentication context for managing:
 * - Authentication state (logged in/out)
 * - JWT token management
 * - Login/logout functions
 * - Auto-authentication check on mount
 */

import type { ReactNode } from 'react';
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

import type { LoginCredentials, LoginResponse } from '../services/api';
import api, { initCsrf } from '../services/api';

/**
 * Admin user interface
 */
export type AdminUser = {
  readonly id: string;
  readonly username: string;
};

/**
 * AdminContext State Interface
 */
export type AdminContextState = {
  // Authentication state
  readonly isAuthenticated: boolean;
  readonly user: AdminUser | null;

  // Loading state
  readonly isLoading: boolean;
  readonly isInitializing: boolean; // True during initial auth check

  // Error state
  readonly error: string | null;

  // Actions
  readonly login: (username: string, password: string) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly checkAuth: () => Promise<boolean>;
  readonly clearError: () => void;
};

/**
 * Create the context with undefined default (will be provided by AdminProvider)
 */
const AdminContext = createContext<AdminContextState | undefined>(undefined);

/**
 * AdminProvider Props
 */
type AdminProviderProps = {
  readonly children: ReactNode;
};

/**
 * AdminProvider Component
 *
 * Wraps the application and provides authentication state management
 * @param root0
 * @param root0.children
 */
export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  // Authentication state (token stored in httpOnly cookie, not in state)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<AdminUser | null>(null);

  // Loading states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);

  // Error state
  const [error, setError] = useState<string | null>(null);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Check authentication status
   * Verifies httpOnly cookie token is valid by calling backend
   */
  const checkAuth = useCallback(async (): Promise<boolean> => {
    try {
      // Call backend to verify the httpOnly cookie token
      const response = await api.auth.verify();

      if (response.valid && response.user) {
        setIsAuthenticated(true);
        setUser(response.user);
        return true;
      }

      // Token invalid or expired
      setIsAuthenticated(false);
      setUser(null);
      return false;
    } catch {
      // If verification fails, clear auth state
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }
  }, []);

  /**
   * Login function
   * Authenticates user with username and password
   * Token is automatically set in httpOnly cookie by backend
   */
  const login = useCallback(async (username: string, password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const credentials: LoginCredentials = { username, password };
      const response: LoginResponse = await api.auth.login(credentials);

      // Backend sets httpOnly cookie automatically
      // Update state with user info from response
      if (response.success && response.user) {
        const adminUser: AdminUser = {
          id: response.user.id,
          username: response.user.username,
        };
        setUser(adminUser);
        setIsAuthenticated(true);

        // Refresh CSRF token after successful login
        // New session requires new CSRF token
        await initCsrf();
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Login failed. Please check your credentials and try again.';
      setError(errorMessage);
      setIsAuthenticated(false);
      setUser(null);
      throw err; // Re-throw so caller can handle
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout function
   * Clears authentication state and httpOnly cookie via backend
   */
  const logout = useCallback(async () => {
    try {
      // Call backend to clear the httpOnly cookie
      await api.auth.logout();
    } catch (error) {
      console.error('Logout request failed:', error);
      // Continue with local cleanup even if backend call fails
    }

    // Clear state
    setIsAuthenticated(false);
    setUser(null);
    setError(null);

    // Refresh CSRF token after logout
    // New session requires new CSRF token
    await initCsrf();
  }, []);

  /**
   * Auto-check authentication on mount
   */
  useEffect(() => {
    const initializeAuth = async () => {
      setIsInitializing(true);
      await checkAuth();
      setIsInitializing(false);
    };

    initializeAuth();
  }, [checkAuth]);

  /**
   * Context value
   */
  const value: AdminContextState = {
    // State
    isAuthenticated,
    user,

    // Loading
    isLoading,
    isInitializing,

    // Error
    error,

    // Actions
    login,
    logout,
    checkAuth,
    clearError,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

/**
 * useAdmin Hook
 *
 * Custom hook to access AdminContext
 * Throws error if used outside of AdminProvider
 *
 * @example
 * const { isAuthenticated, login, logout } = useAdmin();
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAdmin = (): AdminContextState => {
  const context = useContext(AdminContext);

  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }

  return context;
};

export default AdminContext;
